import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { useState, useEffect, useRef, useMemo, useCallback, memo } from "react"

import { rootStore, useStores } from "stores/root-store"

import AlertDialog from "components/common/alert-dialog"
import { LoggingHelper } from "utils/logging-helper"
import { FormWrapper } from "components/common-styles"
import InventoryProductInfo from "./inventory-product-info"
import InventoryDimension from "./inventory-dimension"
import InventoryCrossReference from "./inventory-cross-reference"

import { errorType, trackEvent } from "utils/analytics"
import { storageName } from "shared/constants/storage-constant"
import * as StorageHelper from "utils/storage-helper"
import CommonFormActions from "components/common/common-form-actions"
import { ArrowBack } from "@mui/icons-material"

const InventoryForm = ({ onClose, onSave, creatingBOM }) => {
  const { InventoryStore } = useStores()
  const initialValues = useMemo(() => {
    return {
      product: "",
      item_category1: "",
      item_category2: "",
      item_category3: "",
      long_description: "",
      country_of_origin: "",
      description: "",
      harmonized_code: "",
      lot_controlled: "",
      packing_class: "",
      un_number: "",
      dg_volume: 0,
      dgGoods: false,
      list_price: 0,
    }
  }, [])

  const [openAlert, setOpenAlert] = useState(false)
  const [alertContent, setAlertContent] = useState("")

  const formRef = useRef()
  const dimensionsRef = useRef()
  const crossReferencesRef = useRef()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [productInformation, setProductInformation] = useState(initialValues)
  const [dimensions, setDimensions] = useState([])
  const [wasSuccess, setWasSuccess] = useState(true)

  const newProductRef = useRef("")

  const initData = useCallback(() => {
    if (InventoryStore.productInformation) {
      const data = { ...InventoryStore.productInformation }
      setDimensions(JSON.parse(JSON.stringify(data.dimensions || [])))
      data.dimensionsOld = JSON.parse(JSON.stringify(data.dimensions || []))
      setProductInformation(data)
    } else {
      setProductInformation(initialValues)
    }
  }, [InventoryStore.productInformation, initialValues])

  useEffect(() => {
    initData()
  }, [initData])

  const handleSubmit = async () => {
    const resInfo = await formRef.current.getFormDataIfValid()
    if (!resInfo) return

    const resDimensions = await dimensionsRef.current.getDimensionsIfValid()
    if (!resDimensions) return

    const resCrossReference = await crossReferencesRef.current.getCrossReferencesIfValid()
    if (!resCrossReference) return

    const dimensions = [...resDimensions]
    dimensions.forEach(x => {
      x.cross_reference = resCrossReference
    })

    const newInfo = { ...productInformation, ...resInfo, dimensions: dimensions }

    setIsSubmitting(true)
    setOpenAlert(true)
    const result = await InventoryStore.addOrUpdateData(newInfo)
    LoggingHelper.writeLog("InventoryForm", "submit", `${result ? JSON.stringify(result) : ""}`)

    setWasSuccess(true)
    let msg = InventoryStore.productInformation ? "Data Updated Successfully" : "New Data Added Successfully"
    const acceptedTransactions = result?.acceptedTransactions || result?.resource?.acceptedTransactions || result?.error?.context?.acceptedTransactions || 0
    if (result && (result.resource?.message || result.error?.message || result.message || typeof result == "string") && acceptedTransactions < 1) {
      setWasSuccess(false)
      msg = result.resource?.message || result.error?.message || result.message || result
      trackEvent("record_save", { entity: "product", mode: InventoryStore.productInformation ? "edit" : "add", result: "failed", error_type: errorType(result) })
    } else {
      trackEvent("record_save", { entity: "product", mode: InventoryStore.productInformation ? "edit" : "add", result: "success" })
      newProductRef.current = newInfo.product
      StorageHelper.deleteStorage(storageName.productList)
      await rootStore.loadProductList()
    }

    setIsSubmitting(false)
    setAlertContent(msg)
  }

  const onOK = () => {
    setOpenAlert(false)
    if (wasSuccess) {
      onSave(newProductRef.current)
    }
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <>
      <FormWrapper spacing={2}>
        <Stack direction={"row"} spacing={2}>
          <Tooltip placement="top" title="Back">
            <IconButton onClick={handleClose}>
              <ArrowBack />
            </IconButton>
          </Tooltip>

          <Box>
            {InventoryStore.productInformation ? (
              <>
                <Typography variant="h5">Edit Product: {InventoryStore.productInformation.product}</Typography>
                <small>Review product attributes and dimensions on an existing inventory item</small>
              </>
            ) : (
              <>
                <Typography variant="h5">New Product</Typography>
                <small>Enter product attributes and dimensions to create a new inventory item</small>
              </>
            )}
          </Box>
        </Stack>

        <InventoryProductInfo productInformation={productInformation} ref={formRef} creatingBOM={creatingBOM} />
        <InventoryDimension curDimensions={dimensions} ref={dimensionsRef} isSubmitting={isSubmitting} />
        <InventoryCrossReference dimensions={dimensions} ref={crossReferencesRef} isSubmitting={isSubmitting} />

        <CommonFormActions isSubmitting={isSubmitting} handleSubmit={handleSubmit} handleClose={handleClose} />
      </FormWrapper>
      <AlertDialog isProgress={isSubmitting} content={alertContent} open={openAlert} onClose={() => setOpenAlert(false)} onOK={onOK} />
    </>
  )
}

export default memo(InventoryForm)
